// src/components/checkout/PaymentForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../ThemeProvider';
import Button from '../common/Button';
import { fetchData, fetchNgmktSell, fetchPaymentConfig } from '../../utils/api';

function PaymentForm({ total, onPaymentSuccess, shippingAddress, disabled }) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [paystackKey, setPaystackKey] = useState('');
  const [vat, setVat] = useState(null);
  const [shippingFee, setShippingFee] = useState(null);

  const { formatPrice } = useCurrency();
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  const { primaryColor } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await fetchPaymentConfig();

        const apiKey = config?.api_key || config?.data?.api_key || config?.public_key;
        if (!apiKey) {
          throw new Error('No Paystack public key found in response');
        }

        setPaystackKey(apiKey);

        const business = await fetchData('/website/business-details', { noCache: true });
        setVat(parseFloat(business.fees.vat.vatRate));
        setShippingFee(parseFloat(business.fees.shipping.shippingFee));
      } catch (err) {
        console.error('Payment config load error:', err);
        setError('Failed to load payment settings. Please try again later.');
      }
    };

    loadConfig();
  }, []);

  const vatAmount = vat ? total * (vat / 100) : 0;
  const finalTotal = total + vatAmount + (shippingFee || 0);
  const amountInKobo = Math.round(finalTotal * 100);

  // Use shipping form data if available, fallback to user or defaults
  const customerEmail = shippingAddress?.email || user?.email || 'guest@example.com';
  const customerName = shippingAddress?.name || 
    (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Guest Customer');
  const customerPhone = shippingAddress?.phone || user?.phone_number || '';

  const handlePayment = (e) => {
    e.preventDefault();

    if (disabled || !shippingAddress) {
      setError('Please provide and save your shipping details first.');
      return;
    }

    setError('');
    setIsLoading(true);

    if (!paystackKey) {
      setError('Payment gateway not ready. Please wait or refresh.');
      setIsLoading(false);
      return;
    }

    try {
      if (!window.PaystackPop) {
        setError('Paystack SDK failed to load. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: customerEmail,
        amount: amountInKobo,
        currency: 'NGN',
        ref: `order_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        metadata: {
          custom_fields: [
            { display_name: "Customer Name", variable_name: "customer_name", value: customerName },
            { display_name: "Phone", variable_name: "phone", value: customerPhone },
            { display_name: "Shipping Address", variable_name: "shipping_address", value: JSON.stringify(shippingAddress) },
            { display_name: "Order Total", variable_name: "order_total", value: finalTotal },
          ],
        },
        callback: (response) => {
          if (response.status === 'success') {
            const productsPayload = cart.map((item) => ({
              product_id: item.id,
              name: item.name,
              qty: item.quantity,
              price: item.price,
              has_stock: '0',
              stock: 0,
            }));

            // Structured shipping details object
            const shippingDetails = {
              street: shippingAddress.street || '',
              city: shippingAddress.city || '',
              state: shippingAddress.state || '',
              zip: shippingAddress.zip || '',
              country: shippingAddress.country || 'Nigeria',
              phone: shippingAddress.phone || '',
            };

            const fullShippingAddress = [
              shippingAddress.street?.trim(),
              shippingAddress.city?.trim(),
              shippingAddress.state?.trim(),
              shippingAddress.zip ? shippingAddress.zip.trim() : null,
              shippingAddress.country?.trim() || 'Nigeria',
            ].filter(Boolean).join(', ');

            fetchNgmktSell({
              customer_name: customerName,
              customer_email: customerEmail,
              customer_phone: customerPhone,

              // Preferred way as requested
              address: fullShippingAddress,

              products: productsPayload,
              amount: finalTotal,
              vat: vat,
              logistics: shippingFee,
              payment_method: 'paystack',
              from_wallet: false,
              description: 'Online store purchase',
              platform: 'online',
              auth_code: 54517,
              is_order: false,
              part_payment_amount: 0,
              discount: 0,
              split: [{ split_payment_method: null, split_payment_amount: null, bank_id: null }],
              type: 'full_payment',
              is_split_payment: 0,
              payment_type: null,
              start_date: null,
              end_date: null,
              posting_day: null,
            })
              .then((ngmktRes) => {
                if (!ngmktRes || ngmktRes.status !== 'success') {
                  throw new Error('Order recording failed - invalid backend response');
                }

                // Extract order ID from backend response and format it
                const orderId = ngmktRes.data?.id;
                const receiptNumber = orderId 
                  ? `RV${String(orderId).padStart(8, '0')}` 
                  : `PAY-${response.reference}`;

                const transactionData = {
                  cart,
                  fees: { subtotal: total, vat, shipping: shippingFee, total: finalTotal },
                  shippingAddress,
                  transactionRef: response.reference,
                  receiptNumber,
                  rawOrderId: orderId,
                };

                localStorage.setItem('lastOrder', JSON.stringify(transactionData));

                onPaymentSuccess({ 
                  ...response, 
                  shippingAddress,
                  transactionRef: response.reference,
                  receiptNumber,
                  rawOrderId: orderId
                });

                clearCart();
                navigate('/order-confirmation', {
                  state: {
                    transaction: {
                      ...response,
                      cart,
                      fees: { subtotal: total, vat, shipping: shippingFee, total: finalTotal },
                      shippingAddress,
                      transactionRef: response.reference,
                      receiptNumber,
                      rawOrderId: orderId
                    },
                  },
                });
              })
              .catch((ngmktErr) => {
                console.error('ngmkt recording error:', ngmktErr);
                setError(
                  `Payment was successful, but we couldn't record the order. ` +
                  `Please contact support with this Paystack reference: ${response.reference}`
                );
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            setError('Payment was not successful.');
            setIsLoading(false);
          }
        },
        onClose: () => {
          setError('Payment window was closed.');
          setIsLoading(false);
        },
      });

      handler.openIframe();
    } catch (err) {
      setError('An error occurred while preparing payment. Please try again.');
      console.error('Payment init error:', err);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-200">
      <h2 className="text-2xl font-playfair-display font-semibold text-primary mb-6">
        Complete Your Payment
      </h2>

      <div className="flex items-center gap-4 mb-6">
        <img
          src="/assets/icons/paystack.svg"
          alt="Paystack"
          className="w-10 h-10"
          onError={(e) => (e.target.style.display = 'none')}
        />
        <div>
          <p className="font-montserrat text-lg text-primary font-medium">
            Pay securely with Paystack
          </p>
          <p className="text-sm text-gray-600">Cards, Bank Transfer, USSD</p>
        </div>
      </div>

      <div className="space-y-3 mb-8 font-montserrat text-primary">
        <div className="flex justify-between text-sm">
          <span>Subtotal</span>
          <span>{formatPrice ? formatPrice(total) : `₦${total.toFixed(2)}`}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>VAT ({vat || 0}%)</span>
          <span>{formatPrice ? formatPrice(vatAmount) : `₦${vatAmount.toFixed(2)}`}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Shipping</span>
          <span>{formatPrice ? formatPrice(shippingFee || 0) : `₦${(shippingFee || 0).toFixed(2)}`}</span>
        </div>
        <div className="flex justify-between font-semibold text-lg border-t pt-3 mt-2">
          <span>Total</span>
          <span className="text-primary">
            {formatPrice ? formatPrice(finalTotal) : `₦${finalTotal.toFixed(2)}`}
          </span>
        </div>
      </div>

      {error && (
        <p className="text-red-600 font-montserrat text-sm mb-4 text-center">{error}</p>
      )}

      <Button
        onClick={handlePayment}
        disabled={isLoading || disabled}
        className={`w-full py-4 text-lg font-montserrat rounded-full ${
          disabled
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-primary text-cream hover:bg-primary/90'
        }`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            Processing...
          </span>
        ) : disabled ? (
          'Complete Shipping Details First'
        ) : (
          `Pay ${formatPrice ? formatPrice(finalTotal) : finalTotal.toFixed(0)}`
        )}
      </Button>

      <p className="text-center text-xs text-gray-500 mt-4 font-montserrat">
        Secured by Paystack • Your payment info is safe
      </p>
    </div>
  );
}

export default PaymentForm;